import { saveAs } from "file-saver";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import FadeIn from "../../components/Shared/FadeIn/FadeIn";
import { SIDE_MENU_WIDTH } from "../../components/Shared/SideMenu/SideMenu";
import DetailPageTabs, {
	RouteTabItem,
	ListToolbarAction,
} from "../../components/Shared/DetailPageTabs";
import TagToggleSubMenu from "../../components/Shared/TagMenu/TagToggleSubMenu";
import { APPLICATION_STATUS } from "../../configs/constants/ApplicationStatus";
import { JOB_STATUS } from "../../configs/constants/JobStatus";
import { routes } from "../../configs/constants/routes";
import { useSidebarStore } from "../../hooks/sidebar.hooks";
import Job from "../../models/Job";
import { useCompetencesByCustomer } from "../../networking/customer.hooks";
import {
	useApplications,
	useCancelJob,
	useDeleteJob,
	useDenyJob,
	useJob,
	useReopenJob,
	useSendJobNotification,
	useToggleJobTag,
	useUpdateJob,
} from "../../networking/job.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import DenyReasonModalBody from "./DenyReasonModalBody";
import JobCandidatesTab, { pendingInboxCount } from "./JobCandidatesTab";
import JobHeader from "./JobHeader/JobHeader";
import JobMatchingTab from "./JobMatchingTab";
import JobHistoryTab from "./JobHistoryTab";
import JobOverviewTab from "./JobOverviewTab";
import NotifyConsultantsModal from "./NotifyConsultantsModal/NotifyConsultansModal";
import { apiStore } from "../../stores/api.store";
import { toast } from "../../utils/toast";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

type Params = {
	jobId: string;
};

const JobDetailsPage: React.FC = () => {
	const modalStore = useConfirmModalStore();
	const { mutateAsync: sendJobNotification } = useSendJobNotification();
	const { closeSidebar } = useSidebarStore();

	const { jobId } = useParams<keyof Params>() as Params;
	const { job } = useJob({
		jobId,
		expand: ["shifts", "customer", "contract", "metadata", "applications"],
	});

	const { applications, invitations } = useApplications({
		jobId,
		includeInvitations: true,
	});

	const { competences } = useCompetencesByCustomer({
		customerId: job?.customer.id,
		inherited: true,
	});

	const { data: organisation } = useOrganisation();

	const { mutate: updateJob } = useUpdateJob();
	const { mutate: toggleTag } = useToggleJobTag();
	const { mutate: denyJobMutation } = useDenyJob();
	const { mutate: cancelJobMutation } = useCancelJob();
	const { mutate: deleteJob } = useDeleteJob();
	const { mutate: reopenJobMutation } = useReopenJob();

	const navigate = useNavigate();

	const [isNotifyConsultantsModalOpen, setNotifyConsultantsModalOpen] = useState(false);

	const [sendingNotification, setSendingNotification] = useState(false);

	const { t } = useTranslation();

	const bookedApplication = useMemo(() => {
		if (applications && applications.length > 0)
			return applications.find(({ status }) => status === APPLICATION_STATUS.ACCEPTED);
	}, [applications]);

	// Job tags are descriptive labels without expiry — toggling adds or removes
	// the key (keys reference the org's jobTagTypes). Optimistic: the mutation
	// patches the ["jobs", jobId] cache in onMutate, so the sidebar chips and
	// the dropdown checkboxes flip instantly.
	const toggleJobTag = (job: Job, key: string) => {
		const current = job.tags ?? [];
		const isAssigned = current.some((tag) => tag.key === key);
		const nextTags = isAssigned ? current.filter((tag) => tag.key !== key) : [...current, { key }];

		toggleTag({ jobId, nextTags });
	};

	const jobTagTypes = organisation?.jobTagTypes ?? [];

	const deleteOrderConfirm = () => {
		const deleteConfirmModal: ConfirmModalProps = {
			title: "DELETE_JOB",
			text: job?.status === JOB_STATUS.BOOKED ? "DELETE_JOB_CONFIRM_BOOKED" : "DELETE_JOB_CONFIRM",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => deleteOrder(),
			onReject: () => {},
			type: "DANGER",
		};
		modalStore.newModal(deleteConfirmModal);
	};

	const deleteOrder = async () => {
		deleteJob({ jobId, onSuccess: () => navigate(routes.JOBS) });
	};

	const copyJob = () => {
		if (job) {
			// The create page fetches the job to copy from the ?copy= id.
			navigate(routes.JOB_CREATE + `?copy=${job.id}`);
		}
	};

	const downloadOrderConfirmation = async (type: "consultant" | "customer") => {
		if (!job || !job.applications) return;

		const acceptedApplications = job.applications.filter(
			(a) => a.status === APPLICATION_STATUS.ACCEPTED
		);

		if (acceptedApplications.length === 0) {
			toast.error(t("job.orderConfirmationError"));
			return;
		}

		try {
			if (type === "customer") {
				const pdfBlob = await apiStore.getBlob({
					path: `/job/${job.id}/confirmation/customer`,
					isAuthRequired: true,
				});

				saveAs(pdfBlob, `uppdragsbekraftelse-${job.orderNumber}-kund.pdf`);
			} else {
				for (const application of acceptedApplications) {
					const pdfBlob = await apiStore.getBlob({
						path: `/job/${job.id}/confirmation/consultant/${application.consultant.id}`,
						isAuthRequired: true,
					});

					saveAs(
						pdfBlob,
						`uppdragsbekraftelse-${job.orderNumber}-konsult-${
							application.consultant.firstName || ""
						}-${application.consultant.id.substring(0, 7)}.pdf`
					);
				}
			}
		} catch (err: any) {
			if (err?.error?.code === "NO_TEMPLATE_CONFIGURED") {
				toast.error(t("job.pdfTemplateMissing"));
			} else {
				toast.error(t("job.orderConfirmationError"));
			}
		}
	};

	function getActions(job: Job): ListToolbarAction[] {
		const showOrHideJob: ListToolbarAction = {
			label: job?.hidden ? "SHOW_JOB" : "HIDE_JOB",
			onClick: () => toggleJobVisibilityConfirm(),
		};

		const deleteJob: ListToolbarAction = {
			label: "DELETE_JOB",
			danger: true,
			disabled: job.status === JOB_STATUS.BOOKED,
			onClick: () => deleteOrderConfirm(),
		};

		const cancelJob: ListToolbarAction = {
			label: "CANCEL_JOB",
			onClick: () => cancelJobConfirm(),
		};

		const reOpenJob: ListToolbarAction = {
			label: "RE_OPEN_JOB",
			onClick: () => reopenJobConfirm(),
		};

		const denyJob: ListToolbarAction = {
			label: "DENY_JOB",
			onClick: () => denyJobConfirm(),
		};

		const nofifyMatchingConsultants: ListToolbarAction = {
			label: "job.notifyMatchingConsultants",
			onClick: () => setNotifyConsultantsModalOpen(true),
			dividerBefore: true,
		};

		const copyJobOption: ListToolbarAction = {
			label: "job.copy",
			onClick: () => copyJob(),
		};

		const downloadPdfCustomer: ListToolbarAction = {
			label: "job.downloadOrderConfirmationCustomer",
			onClick: () => downloadOrderConfirmation("customer"),
			dividerBefore: true,
		};

		const downloadPdfConsultant: ListToolbarAction = {
			label: "job.downloadOrderConfirmationConsultant",
			onClick: () => downloadOrderConfirmation("consultant"),
		};

		if ([/* JOB_STATUS.BOOKED, */ JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS].includes(job.status)) {
			return [
				copyJobOption,
				showOrHideJob,
				denyJob,
				cancelJob,
				deleteJob,
				nofifyMatchingConsultants,
				downloadPdfCustomer,
				downloadPdfConsultant,
			];
		} else if (job.status === JOB_STATUS.BOOKED) {
			return [
				copyJobOption,
				showOrHideJob,
				cancelJob,
				deleteJob,
				nofifyMatchingConsultants,
				downloadPdfCustomer,
				downloadPdfConsultant,
			];
		} else if (job.status === JOB_STATUS.CANCELLED) {
			return [reOpenJob, deleteJob];
		} else if (job.status === JOB_STATUS.DECLINED) {
			return [copyJobOption, reOpenJob, deleteJob];
		} else {
			return [
				copyJobOption,
				showOrHideJob,
				denyJob,
				cancelJob,
				deleteJob,
				nofifyMatchingConsultants,
			];
		}
	}

	const toggleJobVisibilityConfirm = () => {
		const hideJobConfirmModal: ConfirmModalProps = {
			title: job?.hidden ? "SHOW_JOB" : "HIDE_JOB",
			text: job?.hidden ? "SHOW_JOB_CONFIRM" : "HIDE_JOB_CONFIRM",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => toggleJobVisibility(),
			onReject: () => {},
			type: "INFO",
		};
		modalStore.newModal(hideJobConfirmModal);
	};

	const toggleJobVisibility = async () => {
		updateJob({
			jobId,
			update: {
				hidden: !job?.hidden,
			},
		});
	};

	const denyJobConfirm = () => {
		const denialReasons = organisation?.denialReasonSettings?.reasons ?? [];

		// Additive / opt-in: only show the reason selector when the org has
		// configured at least one reason. Otherwise the plain confirm modal is
		// unchanged from before.
		if (denialReasons.length === 0) {
			const hideJobConfirmModal: ConfirmModalProps = {
				title: "DENY_JOB",
				text: "DENY_JOB_CONFIRM",
				acceptButtonText: "YES",
				rejectButtonText: "NO",
				onAccept: () => denyJob(),
				onReject: () => {},
				type: "DANGER",
			};
			modalStore.newModal(hideJobConfirmModal);
			return;
		}

		let selection: { reasonKey?: string; reasonText?: string } = {};

		const denyWithReasonModal: ConfirmModalProps = {
			title: "DENY_JOB",
			text: "DENY_JOB_SELECT_REASON",
			body: (
				<DenyReasonModalBody
					reasons={denialReasons}
					allowOther={organisation?.denialReasonSettings?.allowOther}
					onChange={(value) => {
						selection = value;
					}}
				/>
			),
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => denyJob(selection),
			onReject: () => {},
			type: "DANGER",
		};
		modalStore.newModal(denyWithReasonModal);
	};

	const denyJob = async (reason?: { reasonKey?: string; reasonText?: string }) => {
		denyJobMutation({ jobId, ...reason });
	};

	const cancelJobConfirm = () => {
		const hideJobConfirmModal: ConfirmModalProps = {
			title: "CANCEL_JOB",
			text: "CANCEL_JOB_CONFIRM",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => cancelJob(),
			onReject: () => {},
			type: "INFO",
		};
		modalStore.newModal(hideJobConfirmModal);
	};

	const reopenJobConfirm = () => {
		const hideJobConfirmModal: ConfirmModalProps = {
			title: "RE_OPEN_JOB",
			text: "RE_OPEN_JOB_CONFIRM",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => reopenJob(),
			onReject: () => {},
			type: "INFO",
		};
		modalStore.newModal(hideJobConfirmModal);
	};

	const cancelJob = async () => {
		await cancelJobMutation({ jobId });
	};

	const reopenJob = async () => {
		reopenJobMutation({ jobId });
	};

	async function sendNotification(msg: string) {
		if (job) {
			setSendingNotification(true);
			// Fire-and-report like the old store call: failures were swallowed and
			// the success toast always shown, so keep that behavior.
			await sendJobNotification({ jobId: job.id, body: msg }).catch(() => undefined);
			setSendingNotification(false);
			setNotifyConsultantsModalOpen(false);
			toast.success(t("sendNotificationModal.success"));
		}
	}

	if (!job || job.id !== jobId) {
		return null;
	}

	const JOB_TAB_GROUPS: RouteTabItem[] = [
		{
			route: routes.JOB_OVERVIEW,
			label: "OVERVIEW",
			content: <JobOverviewTab job={job} competences={competences || []} />,
		},
		{
			// The consolidated shifts+consultants week grid — owns time and placement.
			route: routes.JOB_SCHEDULE,
			label: "SCHEDULE",
			content: <JobMatchingTab job={job} />,
		},
		{
			// People and decisions: the applications/invitations inbox and the
			// consultant search. Keeps the legacy "consultants" URL segment.
			route: routes.JOB_CONSULTANT,
			label: "CONSULTANTS",
			badge: pendingInboxCount(applications, invitations),
			content: <JobCandidatesTab job={job} />,
		},
		{
			route: routes.JOB_HISTORY,
			label: "EVENTS",
			content: <JobHistoryTab job={job} />,
		},
	];

	return (
		<Styles className="p-4 px-6">
			<FadeIn>
				<React.Fragment>
					<JobHeader
						job={job}
						className="job-header"
						competences={competences || []}
						bookedApplication={bookedApplication}
						onToggleTag={jobTagTypes.length > 0 ? (key) => toggleJobTag(job, key) : undefined}
					/>

					<div className="tab-ctn px-4">
						<DetailPageTabs
							tabs={JOB_TAB_GROUPS}
							actions={getActions(job)}
							actionsExtraItems={
								jobTagTypes.length > 0 && (
									<TagToggleSubMenu
										tagTypes={jobTagTypes}
										assignedTags={job.tags ?? []}
										onToggle={(key) => toggleJobTag(job, key)}
									/>
								)
							}
							onBeforeNavigate={closeSidebar}
							// Job header is out of flow (fixed), so drop the default top
							// margin that separates the tabs from an in-flow header.
							className="mt-0"
						/>
					</div>
				</React.Fragment>

				{isNotifyConsultantsModalOpen && (
					<NotifyConsultantsModal
						onSubmit={sendNotification}
						onClose={() => setNotifyConsultantsModalOpen(false)}
						loading={sendingNotification}
					/>
				)}
			</FadeIn>
		</Styles>
	);
};

export default JobDetailsPage;

const JOB_HEADER_WIDTH = 310;

const Styles = styled.div`
	padding-left: ${JOB_HEADER_WIDTH}px; /* this needs to be ~JobHeader width */

	.job-header {
		width: ${JOB_HEADER_WIDTH}px;
		/* Fixed (not absolute) so it stays anchored to the viewport regardless of
		   the scroll container's positioning context. */
		position: fixed;
		top: 0;
		left: ${SIDE_MENU_WIDTH}px;
		z-index: 10;
		height: 100vh;
		overflow-y: auto;
	}

	.tab-ctn {
	}

	@media (max-width: 1300px) {
		.job-header {
			padding: 1rem 0.8rem;
		}
	}
`;
